import queryString from "query-string";
import { postBusiness, putBusiness, setBusiness } from "./business";
import { updateApplicationLibrary } from "./appifyActions";
import { updateUserApplicationsPermissions } from "./userApplicationsPermissions";
import { setTemplate } from "./platformActions";
import { mapDefaultLinksToLinks } from "../helpers";
import { displayError, removeEmptyParams, dig } from "helpers";
import { privateFetch, deleteLocations, deletePhones } from "apis";

// Application Action
export const resetApplication = () => ({
  type: "RESET_APPLICATION",
});

export const setApplication = (app) => ({
  type: "SET_APPLICATION",
  app,
});

export const updateApplication = (field, value) => ({
  type: "UPDATE_APPLICATION",
  field,
  value,
});

export const updateApplicationImage = (image) => ({
  type: "UPDATE_APPLICATION_IMAGE",
  image,
});

export const updateApplicationLogo = (image) => ({
  type: "UPDATE_APPLICATION_LOGO",
  image,
});

export const updateApplicationInfo = (field, value) => ({
  type: "UPDATE_APPLICATION_INFO",
  field,
  value,
});

export const addApplicationLink = () => ({
  type: "ADD_APPLICATION_LINK",
});

export const removeApplicationLink = (linkIdx) => ({
  type: "REMOVE_APPLICATION_LINK",
  linkIdx,
});

export const updateApplicationLink = (field, linkIdx, value) => ({
  type: "UPDATE_APPLICATION_LINK",
  linkIdx,
  field,
  value,
});

export const updateNavBar = (field, value) => ({
  type: "UPDATE_APPLICATION_NAVBAR",
  field,
  value,
});

export const deleteTemplate = (templateId) => ({
  type: "DELETE_TEMPLATE",
  templateId,
});

export const unpublishTemplate = (templateId) => ({
  type: "UNPUBLISH_TEMPLATE",
  templateId,
});

export const publishTemplate = (templateId, template) => ({
  type: "PUBLISH_TEMPLATE",
  templateId,
  template,
});

export const rmActiveAppPhoneNumber = (locationIdx, phoneIdx) => ({
  type: "REMOVE_ACTIVE_APP_PHONE_NUMBER",
  locationIdx,
  phoneIdx,
});

const removeLocations = async (business, cachedBusiness) => {
  const locations = dig(business, "locations");
  const cachedLocations = dig(cachedBusiness, "locations");
  const locationSet = new Set();
  const cachedLocationSet = new Set();
  locations.forEach((loc) => locationSet.add(loc.id));
  cachedLocations.forEach((loc) => cachedLocationSet.add(loc.id));
  const diff = [];
  cachedLocationSet.forEach((loc) => !locationSet.has(loc) && diff.push(loc));
  const res = await deleteLocations(diff);
  return res;
};

const removePhoneNumbers = async (business, cachedBusiness) => {
  const locations = dig(business, "locations");
  const cachedLocations = dig(cachedBusiness, "locations");
  const phoneNumberSet = new Set();
  const cachedPhoneNumberSet = new Set();
  locations.forEach((location) => {
    const { phoneNumbers } = location;
    phoneNumbers &&
      phoneNumbers.forEach((phone) => phoneNumberSet.add(phone.id));
  });
  cachedLocations.forEach((location) => {
    const { phoneNumbers } = location;
    phoneNumbers &&
      phoneNumbers.forEach((phone) => cachedPhoneNumberSet.add(phone.id));
  });
  const diff = [];
  cachedPhoneNumberSet.forEach(
    (phone) => !phoneNumberSet.has(phone) && diff.push(phone)
  );
  const res = await deletePhones(diff);
  return res;
};

export const postApp = (bizId, app) => {
  const {
    draft,
    hours,
    appCodeAvailable,
    phoneNumbers,
    view,
    address,
    links,
    ...appData
  } = app;

  const appWithBizId = queryString.stringify(
    removeEmptyParams({ bizId, ...appData })
  );
  const filterLinks = links.filter((link) => link.url !== "");

  return new Promise((resolve, reject) => {
    return privateFetch(`${__BASE_API_URL__}/application?${appWithBizId}`, {
      method: "POST",
      body: JSON.stringify({
        unpublishedView: encodeURI(JSON.stringify(view)),
        links: filterLinks,
      }),
    })
      .then((newApp) => {
        try {
          newApp.links = mapDefaultLinksToLinks(newApp.links);
          resolve(newApp);
        } catch (e) {
          reject(e);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const putApp = (app, publish, unpublish) => {
  // excluding params that are not for query params
  const {
    view,
    unpublishedView,
    publishedViews,
    publishedView,
    business,
    links,
    modifiedBy,
    appCodeAvailable,
    modifiedDate,
    createdDate,
    createdBy,
    ...appParams
  } = app;
  // console.log("putApp got app object");
  // console.dir(app);

  const queryParams = queryString.stringify(removeEmptyParams(appParams));
  // console.log(queryParams);
  const filterLinks = links.filter((link) => link.url !== "");
  const parsedView = encodeURI(JSON.stringify(app.view));
  const parsedPublishedView = encodeURI(JSON.stringify(app.publishedViews));

  // console.log(queryParams);
  // console.log(parsedView);
  // console.log(parsedPublishedView);

  let body = JSON.stringify({
    unpublishedView: parsedView,
    links: filterLinks,
  });

  if (publish) {
    body = JSON.stringify({
      unpublishedView: parsedView,
      publishedView: parsedView,
      links: filterLinks,
    });
  }

  if (unpublish) {
    body = JSON.stringify({
      unpublishedView: parsedView,
      publishedView: parsedPublishedView,
      links: filterLinks,
    });
  }
  // console.log("printing query params");
  // console.log(queryParams);

  return new Promise((resolve, reject) => {
    return privateFetch(
      `${__BASE_API_URL__}/application/${app.id}?${queryParams}`,
      {
        method: "PUT",
        body,
      }
    )
      .then((newApp) => {
        try {
          newApp.links = mapDefaultLinksToLinks(newApp.links);
          resolve(newApp);
        } catch (e) {
          reject(e);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const putAppAndDispatch = (app) => (dispatch) => {
  return new Promise((resolve, reject) => {
    return putApp(app).then((newApp) => {
      let updatedApp;
      try {
        newApp.links = mapDefaultLinksToLinks(newApp.links);
        const { publishedView, ...storeAppInfo } = newApp;
        updatedApp = {
          ...storeAppInfo,
          view: JSON.parse(decodeURI(newApp.unpublishedView)),
          publishable: publishedView !== newApp.unpublishedView,
          oldAppCode: newApp.code,
        };
        dispatch(setApplication(updatedApp));
        dispatch(updateApplicationLibrary(updatedApp));
        resolve(updatedApp);
      } catch (e) {
        displayError("Oops! Something went wrong!");
        reject(e);
      }
    });
  });
};

export const putBusinessApp =
  (business, application, publish, unpublish) => async (dispatch) => {
    // console.log("put biz async func got active app");
    // console.dir(application);
    try {
      await removeLocations(business, application.business);
      await removePhoneNumbers(business, application.business);
      const updatedBusiness = await putBusiness(business);
      // console.log("got updated biz after api call");
      // console.dir(updatedBusiness);
      dispatch(setBusiness(updatedBusiness));
      const updatedApp = await putApp(application, publish, unpublish);
      // console.log("got updated app after api call");
      // console.dir(updatedApp);
      let resultApp;
      updatedApp.links = mapDefaultLinksToLinks(updatedApp.links);
      const { publishedView, ...storeAppInfo } = updatedApp;
      resultApp = {
        ...storeAppInfo,
        view: JSON.parse(decodeURI(updatedApp.unpublishedView)),
        publishedViews: JSON.parse(decodeURI(updatedApp.publishedView)),
        publishable: publishedView !== updatedApp.unpublishedView,
        oldAppCode: updatedApp.code,
      };
      // console.log("setting app after api call with result app");
      // console.dir(resultApp);
      dispatch(setApplication(resultApp));
      dispatch(updateApplicationLibrary(resultApp));
      return resultApp;
    } catch (e) {
      console.log(e);
      displayError("Oops! Something went wrong!");
    }
  };

export const postBusinessApp = (business, application) => (dispatch) => {
  // console.log("got app to post");
  // console.log(application);
  return new Promise((resolve, reject) => {
    return postBusiness(business)
      .then((newBusiness) => {
        dispatch(setBusiness(newBusiness));
        return postApp(newBusiness.id, application).then((newApp) => {
          // console.log("new app created");
          // console.log(newApp);
          let updatedApp;
          try {
            newApp.links = mapDefaultLinksToLinks(newApp.links);
            updatedApp = {
              ...newApp,
              view: JSON.parse(decodeURI(newApp.unpublishedView)),
              publishedViews: JSON.parse(decodeURI(newApp.unpublishedView)),
              oldAppCode: newApp.code,
            };
            // console.log("got updated app from postBiz");
            // console.log(updatedApp);

            // set perissions for this app to super admin
            dispatch(updateUserApplicationsPermissions(newApp.id, [0]));

            // make sure redirect goes to template editor home
            dispatch(setTemplate(null, null));
            dispatch(setApplication(updatedApp));
            dispatch(updateApplicationLibrary(updatedApp));
            resolve(updatedApp);
          } catch (reject) {
            displayError("Oops! Something went wrong!");
            reject(e);
          }
        });
      })
      .catch((err) => {
        reject(err);
      });
  });
};
